import { authFetch } from '../../api/csrf';
import { get, post, put, del } from "../apiClient";
import { API_ENDPOINTS } from "../config";

const hrService = {
  // Check if employee ID exists
  checkEmployeeIdExists: async (employeeId) => {
    try {
      const response = await get(`${API_ENDPOINTS.HR.EMPLOYEES}/check-id/${employeeId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: {
            exists: response.data.exists || false,
            employeeId: employeeId
          }
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to check employee ID',
          data: { exists: false }
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to check employee ID',
        data: { exists: false }
      };
    }
  },

  // Add new employee
  addEmployee: async (employeeData) => {
    try {
      const response = await post(API_ENDPOINTS.HR.EMPLOYEES, employeeData);

      if (response.responseCode === 201) {
        return {
          success: true,
          message: response.responseMessage,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to add employee",
          responseCode: response.responseCode,
        };
      }
    } catch (error) {
      

      // Handle specific duplicate error cases
      if (error.response?.status === 409) {
        return {
          success: false,
          error:
            error.response?.data?.responseMessage || "Duplicate entry found",
          responseCode: 409,
          isDuplicate: true,
        };
      }

      return {
        success: false,
        error: error.message || "Failed to add employee",
        responseCode: error.response?.status || 500,
      };
    }
  },

  // Get all employees
  getEmployees: async (queryString = "") => {
    try {
      const url = queryString
        ? `${API_ENDPOINTS.HR.EMPLOYEES}?${queryString}`
        : API_ENDPOINTS.HR.EMPLOYEES;
      
      const response = await get(url);
      
      
      
      
        
      

      if (response.responseCode === 200) {
        
        return {
          success: true,
          responseCode: 200,
          data: response.data, // This contains { employees: [...], pagination: {...} }
          pagination: response.data.pagination,
        };
      } else {
        
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || "Failed to fetch employees",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to fetch employees",
      };
    }
  },

  // Get employees for dropdown (no pagination)
  getEmployeesForDropdown: async () => {
    try {
      
      const response = await get(API_ENDPOINTS.HR.EMPLOYEES_DROPDOWN);
      

      if (response.responseCode === 200) {
        
        return {
          success: true,
          responseCode: 200,
          data: response.data.employees // Direct array of employees
        };
      } else {
        
        return {
          success: false,
          responseCode: response.responseCode,
          error: response.responseMessage || 'Failed to fetch employees for dropdown'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to fetch employees for dropdown'
      };
    }
  },

  // Get employee by ID
  getEmployeeById: async (employeeId) => {
    try {
      const response = await get(`${API_ENDPOINTS.HR.EMPLOYEES}/${employeeId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch employee",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to fetch employee",
      };
    }
  },

  /**
   * Export a single employee's full profile as a backend-generated Excel (.xlsx)
   * workbook and trigger the browser download. The workbook is built entirely on
   * the server; the frontend only requests the file and saves it.
   */
  exportEmployeeDetail: async (employeeId, fallbackName = 'Employee') => {
    try {
      if (!employeeId) {
        return { success: false, error: 'Employee ID is required' };
      }

      const blob = await get(`${API_ENDPOINTS.HR.EMPLOYEES}/${employeeId}/export`, {
        responseType: 'blob',
      });

      const sanitized = String(fallbackName || 'Employee')
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80) || 'Employee';
      const filename = `Employee_${sanitized}.xlsx`;

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to export employee profile',
      };
    }
  },

  getOrgChartRoots: async (params = {}) => {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await get(`${API_ENDPOINTS.HR.ORG_CHART}/roots${query ? `?${query}` : ''}`);
      return {
        success: response.responseCode === 200,
        data: response.data,
        error: response.responseCode === 200 ? null : response.responseMessage
      };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch organization roots' };
    }
  },

  getOrgChartChildren: async (employeeId, params = {}) => {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await get(`${API_ENDPOINTS.HR.ORG_CHART}/${employeeId}/children${query ? `?${query}` : ''}`);
      return {
        success: response.responseCode === 200,
        data: response.data,
        error: response.responseCode === 200 ? null : response.responseMessage
      };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch organization children' };
    }
  },

  getOrgChartPath: async (employeeId, params = {}) => {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await get(`${API_ENDPOINTS.HR.ORG_CHART}/${employeeId}/path${query ? `?${query}` : ''}`);
      return {
        success: response.responseCode === 200,
        data: response.data,
        error: response.responseCode === 200 ? null : response.responseMessage
      };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch organization path' };
    }
  },

  searchOrgChartEmployees: async (params = {}) => {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await get(`${API_ENDPOINTS.HR.ORG_CHART}/search${query ? `?${query}` : ''}`);
      return {
        success: response.responseCode === 200,
        data: response.data,
        error: response.responseCode === 200 ? null : response.responseMessage
      };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to search organization chart' };
    }
  },

  getOrgChartTree: async (params = {}) => {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await get(`${API_ENDPOINTS.HR.ORG_CHART}/tree${query ? `?${query}` : ''}`);
      return {
        success: response.responseCode === 200,
        data: response.data,
        error: response.responseCode === 200 ? null : response.responseMessage
      };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch organization tree' };
    }
  },

  getOrgChartDepartments: async (params = {}) => {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await get(`${API_ENDPOINTS.HR.ORG_CHART}/departments${query ? `?${query}` : ''}`);
      return {
        success: response.responseCode === 200,
        data: response.data,
        error: response.responseCode === 200 ? null : response.responseMessage
      };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch departments' };
    }
  },

  getOrgChartDepartmentEmployees: async (department, params = {}) => {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await get(`${API_ENDPOINTS.HR.ORG_CHART}/departments/${encodeURIComponent(department)}/employees${query ? `?${query}` : ''}`);
      return {
        success: response.responseCode === 200,
        data: response.data,
        error: response.responseCode === 200 ? null : response.responseMessage
      };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch department employees' };
    }
  },

  getOrgChartDesignationLevels: async (params = {}) => {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await get(`${API_ENDPOINTS.HR.ORG_CHART}/designation-levels${query ? `?${query}` : ''}`);
      return {
        success: response.responseCode === 200,
        data: response.data,
        error: response.responseCode === 200 ? null : response.responseMessage
      };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch designation levels' };
    }
  },

  getOrgChartEmployeeSummary: async (employeeId, params = {}) => {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await get(`${API_ENDPOINTS.HR.ORG_CHART}/${employeeId}/summary${query ? `?${query}` : ''}`);
      return {
        success: response.responseCode === 200,
        data: response.data,
        error: response.responseCode === 200 ? null : response.responseMessage
      };
    } catch (error) {
      return { success: false, error: error.message || 'Failed to fetch employee summary' };
    }
  },

  getEmployeeSalaryStructure: async (employeeId) => {
    try {
      const response = await get(
        `${API_ENDPOINTS.HR.EMPLOYEES}/${employeeId}/salary-structure`
      );
      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      }
      return {
        success: false,
        error: response.responseMessage || "Failed to fetch salary structure",
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to fetch salary structure",
      };
    }
  },

  // Update employee
  updateEmployee: async (employeeId, employeeData) => {
    try {
      const response = await put(
        `${API_ENDPOINTS.HR.EMPLOYEES}/${employeeId}`,
        employeeData
      );

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to update employee",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to update employee",
      };
    }
  },

  // Delete employee
  deleteEmployee: async (employeeId) => {
    try {
      const response = await del(`${API_ENDPOINTS.HR.EMPLOYEES}/${employeeId}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to delete employee",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to delete employee",
      };
    }
  },

  // Bulk delete employees
  deleteEmployeesBulk: async (ids) => {
    try {
      const response = await post(`${API_ENDPOINTS.HR.EMPLOYEES}/bulk-delete`, { ids });

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to bulk delete employees",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to bulk delete employees",
      };
    }
  },

  // Bulk status update employees
  updateEmployeesStatusBulk: async (ids, status) => {
    try {
      const response = await post(`${API_ENDPOINTS.HR.EMPLOYEES}/bulk-status-update`, { ids, status });

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to bulk update employee status",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to bulk update employee status",
      };
    }
  },

  // Get departments
  getDepartments: async () => {
    try {
      const response = await get(API_ENDPOINTS.HR.DEPARTMENTS);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch departments",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to fetch departments",
      };
    }
  },

  // Get distinct departments from employees
  getEmployeeDepartmentsDistinct: async () => {
    try {
      const response = await get(`${API_ENDPOINTS.HR.DEPARTMENTS}/distinct-employee`);
      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data.departments || [],
        };
      } else {
        return { success: false, error: response.responseMessage };
      }
    } catch (error) {
      
      return { success: false, error: error.message };
    }
  },

  // Get roles
  getRoles: async () => {
    try {
      const response = await get(API_ENDPOINTS.HR.ROLES);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch roles",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to fetch roles",
      };
    }
  },

  // Get managers
  getManagers: async () => {
    try {
      const response = await get(API_ENDPOINTS.HR.MANAGERS);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch managers",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to fetch managers",
      };
    }
  },

  // Get employee by ID
  getEmployeeById: async (id) => {
    try {
      const response = await get(`${API_ENDPOINTS.HR.EMPLOYEES}/${id}`);

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch employee",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to fetch employee",
      };
    }
  },

  // Update employee
  updateEmployee: async (id, employeeData) => {
    try {
      const response = await put(
        `${API_ENDPOINTS.HR.EMPLOYEES}/${id}`,
        employeeData
      );

      if (response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to update employee",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to update employee",
      };
    }
  },

  // Transfer employee
  transferEmployee: async (id, transferData) => {
    try {
      const response = await post(
        `${API_ENDPOINTS.HR.EMPLOYEES}/${id}/transfer`,
        transferData
      );

      if (response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to transfer employee",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to transfer employee",
      };
    }
  },

  // Get transfer history
  getTransferHistory: async (id) => {
    try {
      const response = await get(
        `${API_ENDPOINTS.HR.EMPLOYEES}/${id}/transfer-history`
      );

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch transfer history",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to fetch transfer history",
      };
    }
  },

  // Document management functions
  getEmployeeDocuments: async (employeeId) => {
    try {
      const response = await get(
        `${API_ENDPOINTS.HR.EMPLOYEES}/${employeeId}/documents`
      );

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data || [],
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to fetch documents",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to fetch documents",
      };
    }
  },

  uploadEmployeeDocument: async (employeeId, documentData) => {
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("name", documentData.name);
      formData.append("type", documentData.type);
      formData.append("description", documentData.description);
      formData.append("file", documentData.file);

      // Get token from localStorage or sessionStorage
            const response = await authFetch(
        `${API_ENDPOINTS.HR.EMPLOYEES}/${employeeId}/documents`, {
        credentials: 'include',
          method: "POST",
          
          body: formData,
        }
      );

      const result = await response.json();

      if (result.responseCode === 200 || result.responseCode === 201) {
        return {
          success: true,
          data: result.data,
        };
      } else if (result.responseCode === 401) {
        // Token expired, redirect to login
        window.location.href = "/login";
        return {
          success: false,
          error: "Session expired. Please login again.",
        };
      } else {
        return {
          success: false,
          error: result.responseMessage || 'Failed to upload document'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to upload document'
      };
    }
  },

  // Download employee document
  downloadEmployeeDocument: async (employeeId, documentId) => {
    try {
      // Get token from localStorage or sessionStorage
            const response = await authFetch(`${API_ENDPOINTS.HR.EMPLOYEES}/${employeeId}/documents/${documentId}/download`, {
        credentials: 'include',
        method: 'GET',
        
        redirect: 'follow' // Follow redirects automatically
      });

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        const contentDisposition = response.headers.get('content-disposition');
        const finalUrl = response.url;

        // Check if the final URL is a Cloudinary URL (redirect happened)
        if (finalUrl && finalUrl.includes('cloudinary.com')) {
          // For Cloudinary URLs, create a download link with the original filename
          const link = document.createElement('a');
          link.href = finalUrl;

          // Extract filename from content-disposition or use document ID
          let filename = `document-${documentId}`;
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '');
            }
          }

          link.download = filename;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          return {
            success: true,
            message: 'Document download initiated'
          };
        }

        // For direct file downloads (blob response)
        try {
          const blob = await response.blob();

          // Check if blob is actually a file or an error response
          if (blob.type === 'application/json') {
            // It might be an error response
            const text = await blob.text();
            try {
              const errorData = JSON.parse(text);
              return {
                success: false,
                error: errorData.responseMessage || 'Failed to download document'
              };
            } catch (e) {
              // Not JSON, continue with download
            }
          }

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;

          // Extract filename from content-disposition header if available
          let filename = `document-${documentId}`;
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '');
            }
          }

          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          return {
            success: true,
            message: 'Document downloaded successfully'
          };
        } catch (blobError) {
          
          return {
            success: false,
            error: 'Failed to process document download'
          };
        }
      } else {
        const errorData = await response.json().catch(() => ({ responseMessage: 'Failed to download document' }));
        return {
          success: false,
          error: errorData.responseMessage || `Failed to download document (Status: ${response.status})`
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to download document'
      };
    }
  },

  deleteEmployeeDocument: async (employeeId, documentId) => {
    try {
      const response = await del(
        `${API_ENDPOINTS.HR.EMPLOYEES}/${employeeId}/documents/${documentId}`
      );

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to delete document",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to delete document",
      };
    }
  },

  // Bulk upload functions
  bulkUploadEmployees: async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await post(
        `${API_ENDPOINTS.HR.EMPLOYEES}/bulk-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to upload file",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to upload file",
      };
    }
  },

  bulkImportEmployees: async (validRows) => {
    try {
      const response = await post(`${API_ENDPOINTS.HR.EMPLOYEES}/bulk-import`, {
        validRows,
      });

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to import employees",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to import employees",
      };
    }
  },

  downloadBulkTemplate: async () => {
    try {
      const response = await authFetch(`${API_ENDPOINTS.HR.EMPLOYEES}/bulk-template`, {
        credentials: 'include',
        method: 'GET',
        headers: {}
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const date = new Date();
        const hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const formattedHours = (hours % 12 || 12).toString().padStart(2, '0');
        const formattedDate = `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}_${formattedHours}-${minutes}${ampm}`;
        link.setAttribute('download', `employee_bulk_upload_template_${formattedDate}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return { success: true };
      } else {
        return {
          success: false,
          error: 'Failed to download template'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to download template",
      };
    }
  },

  // Bulk upload validation
  bulkUploadValidation: async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await post(
        `${API_ENDPOINTS.HR.EMPLOYEES}/bulk-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to validate file",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to validate file",
      };
    }
  },

  // Bulk import employees
  bulkImportEmployees: async (validRows) => {
    try {
      const response = await post(`${API_ENDPOINTS.HR.EMPLOYEES}/bulk-import`, {
        validRows,
      });

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to import employees",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to import employees",
      };
    }
  },

  // Get employee payroll
  getEmployeePayroll: async (employeeId) => {
    try {
      const response = await get(
        `${API_ENDPOINTS.HR.EMPLOYEES}/${employeeId}/payroll`
      );

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to load payroll data",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to load payroll data",
      };
    }
  },

  // Get employee leaves
  getEmployeeLeaves: async (employeeId) => {
    try {
      const response = await get(
        `${API_ENDPOINTS.HR.EMPLOYEES}/${employeeId}/leaves`
      );

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to load leave data",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to load leave data",
      };
    }
  },

  // Get employee attendance
  getEmployeeAttendance: async (employeeId) => {
    try {
      const response = await get(
        `${API_ENDPOINTS.HR.EMPLOYEES}/${employeeId}/attendance`
      );

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to load attendance data",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to load attendance data",
      };
    }
  },

  // Get employee performance
  getEmployeePerformance: async (employeeId) => {
    try {
      const response = await get(
        `${API_ENDPOINTS.HR.EMPLOYEES}/${employeeId}/performance`
      );

      if (response.responseCode === 200) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || "Failed to load performance data",
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || "Failed to load performance data",
      };
    }
  },
};

export default hrService;
